import {
  Button,
  DataTable,
  DataTableColumnHeader,
  DataTableSkeleton,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  useDataTable,
} from '@data-delivery/d3s';
import type { AssignmentEntity, PermissionApi } from '@data-delivery/permission-ui-api';
import { assignmentsQueries } from '@data-delivery/permission-ui-data-access-state';
import { useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { AlertTriangle, FolderOpen, RefreshCcw } from 'lucide-react';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useBusinessGroupPermission,
  WithBusinessGroupPermission,
} from '../business-group-permission-context';
import { WithPermission } from '../with-permission';

export interface AssignmentsSidebarProps {
  assignments: AssignmentEntity[];
  loading: boolean;
  error: boolean;
  errorObj: unknown;
  selectedId?: string;
  persistedSelectedAssignmentId?: string;
  businessGroupId?: string;
  onSelectAssignment?: (id: string) => void;
  onNavigateAssignment?: (id: string) => void;
  onCreateAssignment?: () => void;
  userRoles?: PermissionApi.BusinessGroupRoleEntity[];
}

export function AssignmentsSidebar({
  assignments,
  loading,
  error,
  errorObj,
  selectedId,
  persistedSelectedAssignmentId,
  businessGroupId,
  onSelectAssignment,
  onNavigateAssignment,
  onCreateAssignment,
  userRoles = [],
}: AssignmentsSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const bgPermissionContext = useBusinessGroupPermission();
  const PermissionWrapper = bgPermissionContext ? WithBusinessGroupPermission : WithPermission;
  type Row = AssignmentEntity;
  const columns = useMemo<Array<ColumnDef<Row>>>(
    () => [
      {
        accessorKey: 'lastModified',
        sortingFn(a, b) {
          const aVal =
            (
              a.original as unknown as {
                lastModified?: number;
                modifiedAt?: number;
                updatedAt?: number;
              }
            ).lastModified ??
            (a.original as unknown as { modifiedAt?: number }).modifiedAt ??
            (a.original as unknown as { updatedAt?: number }).updatedAt ??
            0;
          const bVal =
            (
              b.original as unknown as {
                lastModified?: number;
                modifiedAt?: number;
                updatedAt?: number;
              }
            ).lastModified ??
            (b.original as unknown as { modifiedAt?: number }).modifiedAt ??
            (b.original as unknown as { updatedAt?: number }).updatedAt ??
            0;
          return aVal === bVal ? 0 : aVal < bVal ? -1 : 1;
        },

        header: () => null,
        cell: () => null,
        enableSorting: true,
      },
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title='Assignments' hide={false} />
        ),
        cell({ row }) {
          const active = row.original.id === (selectedId || persistedSelectedAssignmentId);
          return (
            <div className={`flex flex-col rounded-sm px-2 py-1 ${active ? 'bg-accent/40' : ''}`}>
              <span className='font-medium'>{row.original.name}</span>
              <span className='text-xs text-muted-foreground'>{row.original.id}</span>
            </div>
          );
        },
        enableSorting: false,
      },
    ],
    [selectedId, persistedSelectedAssignmentId],
  );
  const table = useDataTable<Row>({
    data: assignments,
    columns,
    getRowId: r => r.id,
    onRowClick(r) {
      if (onSelectAssignment) {
        onSelectAssignment(r.id);
      }

      if (onNavigateAssignment) {
        onNavigateAssignment(r.id);
        return;
      }

      if (businessGroupId) {
        navigate(
          `/business-groups/${encodeURIComponent(businessGroupId)}/assignments/${encodeURIComponent(
            r.id,
          )}`,
        );
      }
    },
    initialState: {
      columnVisibility: { lastModified: false },
      sorting: [{ id: 'lastModified', desc: true }],
    },
  });
  const prefetch = (id: string) => {
    if (!businessGroupId) {
      return;
    }

    queryClient.prefetchQuery(assignmentsQueries.byBusinessGroup(businessGroupId));
  };

  return (
    <div className='flex w-80 flex-col rounded-md border'>
      <div className='flex items-center justify-between gap-2 border-b px-2 py-2'>
        <span className='text-xs font-semibold tracking-wide'>Assignments</span>
        {onCreateAssignment ? (
          <PermissionWrapper roles={userRoles} resource='assignments' action='create'>
            <Button
              size='sm'
              onClick={() => {
                onCreateAssignment();
              }}
            >
              Create
            </Button>
          </PermissionWrapper>
        ) : businessGroupId ? (
          <PermissionWrapper roles={userRoles} resource='assignments' action='create'>
            <Button
              size='sm'
              onClick={() => {
                navigate(`/business-groups/${businessGroupId}/assignments/new`, {
                  state: { backgroundLocation: location },
                });
              }}
            >
              Create
            </Button>
          </PermissionWrapper>
        ) : null}
      </div>
      {loading ? (
        <DataTableSkeleton
          withPagination={false}
          rowCount={10}
          columnCount={1}
          searchableColumnCount={0}
          filterableColumnCount={0}
          showViewOptions={false}
          cellWidths={['16rem']}
          shrinkZero
        />
      ) : error ? (
        <Empty className='h-[calc(100vh-250px)] border-0'>
          <EmptyHeader>
            <EmptyMedia variant='icon'>
              <AlertTriangle className='h-8 w-8 text-destructive' />
            </EmptyMedia>
            <EmptyTitle>Error loading assignments</EmptyTitle>
            <EmptyDescription>
              {(() => {
                if (typeof errorObj === 'object' && errorObj && 'message' in errorObj) {
                  const msg = (errorObj as { message?: unknown }).message;
                  return typeof msg === 'string' ? msg : 'Unknown error';
                }

                return 'Unknown error';
              })()}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <div className='flex gap-2'>
              <Button
                size='sm'
                variant='secondary'
                onClick={() => {
                  if (businessGroupId) {
                    queryClient.invalidateQueries(
                      assignmentsQueries.byBusinessGroup(businessGroupId),
                    );
                  }
                }}
              >
                <RefreshCcw className='mr-1 h-4 w-4' /> Retry
              </Button>
            </div>
          </EmptyContent>
        </Empty>
      ) : assignments.length === 0 ? (
        <Empty className='h-[calc(100vh-250px)] border-0'>
          <EmptyHeader>
            <EmptyMedia variant='icon'>
              <FolderOpen className='h-8 w-8 text-muted-foreground' />
            </EmptyMedia>
            <EmptyTitle>No assignments</EmptyTitle>
            <EmptyDescription>
              {businessGroupId
                ? 'This business group has no assignments yet.'
                : 'Select a business group to view its assignments.'}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            {businessGroupId && (
              <div className='flex gap-2'>
                <PermissionWrapper roles={userRoles} resource='assignments' action='create'>
                  <Button
                    size='sm'
                    variant='secondary'
                    onClick={() => {
                      navigate(`/business-groups/${businessGroupId}/assignments/new`, {
                        state: { backgroundLocation: location },
                      });
                    }}
                  >
                    Create Assignment
                  </Button>
                </PermissionWrapper>
              </div>
            )}
          </EmptyContent>
        </Empty>
      ) : (
        <DataTable
          table={table}
          className='h-[calc(100vh-250px)]'
          emptyPlaceholderTitle='Assignments'
          emptyPlaceholderDescription='No assignments.'
          getRowProps={row => ({
            onMouseEnter() {
              prefetch(row.id);
            },
          })}
        />
      )}
    </div>
  );
}

export default AssignmentsSidebar;
